import type { UseMutationOptions } from '@tanstack/react-query'

import { useMutationApi, useQueryApi } from 'utils/api'
import { handleCommonErrors } from 'utils/api'

import type { CommonListResponse, CommonResponse } from 'typings/api'
import type { TMilCommonErrorResponse, TTopicList } from 'typings/microlearning'
import type {
  TAPIGetPPGDaljabQuestions,
  TAPIGetPPGDaljabSession,
  TAPIGetProfilePPGDaljabResponse,
  TAPIGetSJTPPGDaljabResponse,
  TAPIPostPPGDaljabSession,
} from 'typings/ppg-daljab'

export function useGetContextPPGDaljab() {
  const profile = useGetProfilePPGDaljab()

  const profileStatusCode =
    profile.failureReason?.originalError?.response?.status || 0
  const isError = profile.isError && profileStatusCode !== 404
  return {
    data: profile.data?.data,
    isLoading: profile.isLoading,
    isError,
    isEligible: !profile.isError,
  }
}

export function useGetProfilePPGDaljab() {
  return useQueryApi<
    CommonResponse<TAPIGetProfilePPGDaljabResponse>,
    TMilCommonErrorResponse
  >(`/microlearnings/ppg-daljab/profile`, {
    queryConfig: {
      useErrorBoundary: false,
    },
  })
}

export function useGetSJTPPGDaljab(topicId: string | number) {
  return useQueryApi<
    CommonListResponse<TAPIGetSJTPPGDaljabResponse>,
    TMilCommonErrorResponse
  >(`/microlearnings/topic/${topicId}/sjt-question-groups`, {
    queryConfig: {
      enabled: !!topicId,
      useErrorBoundary: false,
    },
  })
}

export function useAPIPPGDaljabTopicList(enabled = true) {
  return useQueryApi<
    CommonListResponse<TTopicList, { urlToEnroll: string; urlTryOut: string }>
  >('/microlearnings/topic/v1/list-daljab', {
    queryConfig: {
      enabled,
      useErrorBoundary: false,
    },
    errorHandlerFn: (status, path, error) => {
      handleCommonErrors(status ?? 500, path, error)
    },
  })
}

export function useAPIPPGDaljabQuestions(
  topicId: string | number,
  groupId: string | number,
  config?: any
) {
  return useQueryApi<
    CommonResponse<TAPIGetPPGDaljabQuestions[]>,
    TMilCommonErrorResponse
  >(
    `/microlearnings/topic/${topicId}/sjt-question-groups/${groupId}/questions`,
    {
      queryConfig: {
        enabled: !!groupId,
        useErrorBoundary: true,
        ...config,
      },
    }
  )
}

export function useAPIPPGDaljabAnswers(
  topicId: string | number,
  groupId: string | number,
  config?: any
) {
  return useQueryApi<
    CommonResponse<TAPIGetPPGDaljabQuestions[]>,
    TMilCommonErrorResponse
  >(`/microlearnings/topic/${topicId}/sjt-question-groups/${groupId}/answers`, {
    queryConfig: {
      enabled: !!groupId,
      useErrorBoundary: true,
      ...config,
    },
  })
}

export function useAPIPPGDaljabSession(
  topicId: string | number,
  groupId: string | number
) {
  return useQueryApi<
    CommonResponse<TAPIGetPPGDaljabSession | null>,
    TMilCommonErrorResponse
  >(
    `/microlearnings/topics/${topicId}/sjt-question-group/${groupId}/sjt-sessions`,
    {
      queryConfig: {
        enabled: !!groupId,
        useErrorBoundary: false,
      },
    }
  )
}

export function useAPIPPGDaljabSessionPost(
  opts: UseMutationOptions<
    unknown,
    TMilCommonErrorResponse,
    TAPIPostPPGDaljabSession
  >
) {
  return useMutationApi<
    unknown,
    TMilCommonErrorResponse,
    TAPIPostPPGDaljabSession
  >(`/microlearnings/sjt-sessions`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: opts,
  })
}

export function useAPIPPGDaljabCheckingNIK(
  opts: UseMutationOptions<
    CommonResponse<string[]>,
    TMilCommonErrorResponse,
    { nik: string }
  >
) {
  return useMutationApi<
    CommonResponse<string[]>,
    TMilCommonErrorResponse,
    { nik: string }
  >(`/microlearnings/ppg-daljab/check-eligible`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      ...opts,
      useErrorBoundary: false,
    },
  })
}
